import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Loader2, Send, Mic } from "lucide-react";
import axios from "axios";

const API = "/api";

const RATING_DIMENSIONS = [
  { key: "impact", q: "Out of 10 — how much did your work actually move the needle this year? A 7 is strong. A 10 means you changed the game." },
  { key: "innovation", q: "Out of 10 — how much did you bring something new this year? New ideas, new approaches, new ways of thinking." },
  { key: "ownership", q: "Out of 10 — how fully did you own your outcomes? Not tasks. Outcomes." },
  { key: "collaboration", q: "Out of 10 — how well did you make the people around you better?" },
  { key: "growth", q: "Out of 10 — how much did you grow this year? Skills, perspective, depth." },
  { key: "reliability", q: "Out of 10 — when people counted on you, did you come through?" },
];

function SparkleIcon({ size = 24, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
      <path d="M12 2L13.09 8.26L18 6L14.74 10.91L21 12L14.74 13.09L18 18L13.09 15.74L12 22L10.91 15.74L6 18L9.26 13.09L3 12L9.26 10.91L6 6L10.91 8.26L12 2Z" fill="currentColor" />
    </svg>
  );
}

/* Completed Mirror Display */
function CompletedMirror({ data, token }) {
  return (
    <div data-testid="completed-mirror" className="max-w-2xl mx-auto px-6 md:px-12 py-12 md:py-20">
      <div className="mb-12">
        <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold mb-3" style={{ color: '#E91E8C' }}>
          Your Mirror &middot; {data.fy}
        </p>
        <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#16130F] mb-2">
          {data.person_name}
        </h1>
        <p className="text-sm font-sans" style={{ color: '#4B5563' }}>
          Completed {new Date(data.completed_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
        </p>
      </div>

      {/* Mira's Reflections */}
      {data.mira_reflection && (
        <div className="mb-14 rounded-2xl overflow-hidden" style={{ background: '#16130F' }}>
          <div className="px-8 md:px-10 py-8 md:py-10">
            <div className="flex items-center gap-2.5 mb-6">
              <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: '#E91E8C' }}>
                <SparkleIcon size={14} className="text-white" />
              </div>
              <p className="text-xs uppercase tracking-[0.2em] font-sans font-semibold" style={{ color: '#E91E8C' }}>What Mira sees</p>
            </div>
            <p className="font-sans text-base md:text-lg leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.85)', lineHeight: '1.9' }}>
              {data.mira_reflection.mirror_reflection}
            </p>
            <div className="py-6" style={{ borderTop: '0.5px solid rgba(255,255,255,0.1)' }}>
              <p className="text-xs uppercase tracking-[0.15em] font-sans font-semibold mb-2" style={{ color: '#E91E8C' }}>Your theme</p>
              <p className="font-sans text-lg italic" style={{ color: 'rgba(255,255,255,0.9)' }}>
                {data.mira_reflection.mirror_theme}
              </p>
            </div>
            {data.road_ahead?.one_word && (
              <div className="pt-6" style={{ borderTop: '0.5px solid rgba(255,255,255,0.1)' }}>
                <p className="text-xs uppercase tracking-[0.15em] font-sans font-semibold mb-2" style={{ color: '#E91E8C' }}>
                  Your word: {data.road_ahead.one_word}
                </p>
                <p className="font-sans text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)', lineHeight: '1.8' }}>
                  {data.mira_reflection.word_insight}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Previous words journey */}
      {data.previous_words && data.previous_words.length > 0 && (
        <div className="mb-10 rounded-xl p-6" style={{ background: '#FAEEDA' }}>
          <p className="text-xs uppercase tracking-[0.15em] font-sans font-semibold mb-3" style={{ color: '#E91E8C' }}>Your journey in words</p>
          <div className="flex flex-wrap gap-3">
            {data.previous_words.map((pw) => (
              <span key={pw.fy} className="px-3 py-1.5 rounded-full text-sm font-sans" style={{ background: 'rgba(233,30,140,0.15)', color: '#E91E8C' }}>
                {pw.fy}: <strong>{pw.word}</strong>
              </span>
            ))}
            <span className="px-3 py-1.5 rounded-full text-sm font-sans font-semibold" style={{ background: '#E91E8C', color: '#fff' }}>
              {data.fy}: {data.road_ahead?.one_word}
            </span>
          </div>
        </div>
      )}

      {/* Self Ratings */}
      {data.self_ratings && (
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold mb-5" style={{ color: '#E91E8C' }}>Your honest numbers</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {Object.entries(data.self_ratings).filter(([k]) => k !== "average").map(([k, v]) => (
              <div key={k} className="text-center p-4 rounded-xl" style={{ background: '#FBF7F0', border: '0.5px solid #E5E7EB' }}>
                <p className="text-2xl font-sans font-semibold" style={{ color: '#E91E8C' }}>{v}</p>
                <p className="text-xs uppercase tracking-[0.1em] font-sans mt-1" style={{ color: '#4B5563' }}>{k}</p>
              </div>
            ))}
          </div>
          {data.self_ratings.average && (
            <p className="text-center mt-4 text-sm font-sans" style={{ color: '#4B5563' }}>
              Average: <strong style={{ color: '#E91E8C' }}>{data.self_ratings.average}/10</strong>
            </p>
          )}
        </div>
      )}

      {/* Download + Privacy */}
      <div className="mt-16 pt-8 text-center" style={{ borderTop: '0.5px solid #E5E7EB' }}>
        {token && (
          <a
            href={`${window.location.origin}/api/mirror/document/${token}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-mirror-completed"
            className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full text-base font-sans font-semibold text-white transition-all hover:scale-105 mb-6"
            style={{ background: '#E91E8C', textDecoration: 'none' }}
          >
            Download my Mirror &rarr;
          </a>
        )}
        <p className="font-sans text-xs italic" style={{ color: '#888780' }}>
          Your Mirror belongs entirely to you. Mira reads it to reflect it back — and then it stays in your hands. We will never share it, sell it, or show it to anyone — including your employer. Ever.
        </p>
      </div>
    </div>
  );
}

/* Mirror Conversation Component */
function MirrorConversation({ storyInfo, token }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [step, setStep] = useState("intro"); // intro, rearview_1, rearview_2, rearview_3, rating_0..5, road_ahead, needs, word, word_why, saving, done
  const [chipSet, setChipSet] = useState("intro");
  const [showChips, setShowChips] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Collected data
  const mirrorData = useRef({
    rearview: { excellence: "", near_misses: "", failures: "" },
    self_ratings: {},
    road_ahead: { big_bets: "", quarterly_milestones: "", needs: "", one_word: "" },
    transcript: [],
  });
  const [ratingIndex, setRatingIndex] = useState(0);
  const [reflections, setReflections] = useState(null);

  const chips = {
    intro: [
      { label: "Completely honest", text: "Completely honest", style: "amber" },
      { label: "I'll try", text: "I'll try", style: "charcoal" },
    ],
    rating: Array.from({ length: 10 }, (_, i) => ({
      label: String(i + 1),
      text: String(i + 1),
      style: i + 1 >= 7 ? "amber" : "charcoal",
    })),
    none: [],
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, showChips]);

  useEffect(() => {
    // Opening message — personalized with their name
    const name = storyInfo?.person_name || "";
    const opening = name
      ? `Welcome back ${name}. This is your private space — just you and me. No one else will read what you share here.\n\nI want to walk you through your year. How honest are you willing to be with yourself today?`
      : `Welcome back. This is your private space — just you and me. No one else will read what you share here.\n\nI want to walk you through your year. How honest are you willing to be with yourself today?`;
    setMessages([{ role: "mira", text: opening }]);
    mirrorData.current.transcript.push({ role: "mira", text: opening });
  }, [storyInfo]);

  const stripMd = (text) => {
    if (!text) return text;
    return text.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1').replace(/__(.+?)__/g, '$1').replace(/_(.+?)_/g, '$1').replace(/#{1,3}\s*/g, '').replace(/`(.+?)`/g, '$1');
  };

  const addMira = useCallback((text, newChipSet) => {
    const clean = stripMd(text);
    setMessages(prev => [...prev, { role: "mira", text: clean }]);
    mirrorData.current.transcript.push({ role: "mira", text: clean });
    if (newChipSet !== undefined) {
      setChipSet(newChipSet);
      setShowChips(false);
      setTimeout(() => setShowChips(true), 300);
    }
  }, []);

  const addUser = (text) => {
    setMessages(prev => [...prev, { role: "user", text }]);
    mirrorData.current.transcript.push({ role: "user", text });
  };

  const getMiraResponse = async (message, section) => {
    try {
      const res = await axios.post(`${API}/mirror/chat`, { message, session_id: sessionId, section });
      setSessionId(res.data.session_id);
      return res.data.reply;
    } catch {
      return null;
    }
  };

  const addMiraThenQuestion = useCallback(async (ack, question, chipSet) => {
    if (ack) {
      addMira(ack, undefined);
      await new Promise(r => setTimeout(r, 800));
    }
    addMira(question, chipSet);
  }, [addMira]);

  const processStep = async (text) => {
    addUser(text);
    setInput("");
    setLoading(true);

    switch (step) {
      case "intro": {
        const ack = await getMiraResponse(text, "intro");
        const q = "Let's look back at the last 12 months. Where did your work have real, visible impact? Don't be modest — name the contribution, the outcome, and why it mattered.";
        await addMiraThenQuestion(ack, q, "none");
        setStep("rearview_1");
        break;
      }
      case "rearview_1": {
        mirrorData.current.rearview.excellence = text;
        const ack = await getMiraResponse(text, "rearview");
        const q = "What nearly worked — but didn't quite make it? The things that landed at 60% instead of 100%.";
        await addMiraThenQuestion(ack, q, "none");
        setStep("rearview_2");
        break;
      }
      case "rearview_2": {
        mirrorData.current.rearview.near_misses = text;
        const ack = await getMiraResponse(text, "rearview");
        const q = "Now the one I really want you to sit with. What genuinely failed? Not what you'd tell your manager. What you'd tell yourself at midnight.";
        await addMiraThenQuestion(ack, q, "none");
        setStep("rearview_3");
        break;
      }
      case "rearview_3": {
        mirrorData.current.rearview.failures = text;
        const ack = await getMiraResponse(text, "rearview");
        const dim = RATING_DIMENSIONS[0];
        if (ack) {
          addMira(ack, undefined);
          await new Promise(r => setTimeout(r, 800));
          addMira("Now let's put some honest numbers to your year.", undefined);
          await new Promise(r => setTimeout(r, 600));
        }
        addMira(dim.q, "rating");
        setStep("rating");
        setRatingIndex(0);
        break;
      }
      case "rating": {
        const num = parseInt(text);
        const dim = RATING_DIMENSIONS[ratingIndex];
        mirrorData.current.self_ratings[dim.key] = isNaN(num) ? 5 : Math.min(10, Math.max(1, num));

        const nextIdx = ratingIndex + 1;
        if (nextIdx < RATING_DIMENSIONS.length) {
          const ack = await getMiraResponse(`I rated myself ${num} on ${dim.key}`, "rating");
          const nextDim = RATING_DIMENSIONS[nextIdx];
          await addMiraThenQuestion(ack, nextDim.q, "rating");
          setRatingIndex(nextIdx);
        } else {
          const ack = await getMiraResponse(`I rated myself ${num} on ${dim.key}`, "rating");
          const q = "What are the three most important things you want to deliver in the next 12 months? Not activities. Outcomes.";
          if (ack) {
            addMira(ack, undefined);
            await new Promise(r => setTimeout(r, 800));
            addMira("Now let's look forward.", undefined);
            await new Promise(r => setTimeout(r, 600));
          }
          addMira(q, "none");
          setStep("road_ahead");
        }
        break;
      }
      case "road_ahead": {
        mirrorData.current.road_ahead.big_bets = text;
        const ack = await getMiraResponse(text, "road_ahead");
        const q = "What would make the biggest difference to your next 12 months — skills, support, or something that needs to change?";
        await addMiraThenQuestion(ack, q, "none");
        setStep("needs");
        break;
      }
      case "needs": {
        mirrorData.current.road_ahead.needs = text;
        const ack = await getMiraResponse(text, "needs");
        const q = "Last question. If you had to pick one word to define your theme for the year ahead — a word you'd come back to when things get noisy — what would it be?";
        await addMiraThenQuestion(ack, q, "none");
        setStep("word");
        break;
      }
      case "word": {
        mirrorData.current.road_ahead.one_word = text.trim();
        const word = text.trim();
        addMira(`${word}. Tell me why that word.`, "none");
        setStep("word_why");
        break;
      }
      case "word_why": {
        // Save everything
        setStep("saving");
        addMira("Thank you. Let me hold your year up to the light for a moment...", "none");

        const transcript = mirrorData.current.transcript.map(t => `${t.role === "user" ? "You" : "Mira"}: ${t.text}`).join("\n");

        try {
          const res = await axios.post(`${API}/mirror/save`, {
            token,
            rearview: mirrorData.current.rearview,
            self_ratings: mirrorData.current.self_ratings,
            road_ahead: mirrorData.current.road_ahead,
            raw_conversation: transcript,
          });

          setReflections(res.data.mira_reflection);

          const name = storyInfo?.person_name || "";
          const ref = res.data.mira_reflection;
          const reflectionMsg = `Here is what I noticed. Not as a score. Not as feedback. Just — what I see when I hold your year up to the light.\n\n${ref.mirror_reflection || ""}\n\n${ref.mirror_theme ? `Your theme: ${ref.mirror_theme}` : ""}\n\n${ref.word_insight ? `Your word — ${mirrorData.current.road_ahead.one_word}: ${ref.word_insight}` : ""}`;
          addMira(reflectionMsg, "none");
          
          // Final message with download offer
          await new Promise(r => setTimeout(r, 1200));
          const doneMsg = name
            ? `Your Mirror is complete. ${name}, this was brave. Here is your document — yours to keep, print, or return to whenever you need it. Nobody else will ever read it.`
            : `Your Mirror is complete. This was brave. Here is your document — yours to keep, print, or return to whenever you need it. Nobody else will ever read it.`;
          addMira(doneMsg, "none");
          setStep("done");
        } catch {
          addMira("Something went wrong saving your Mirror. Please try again.", "none");
          setStep("word_why");
        }
        break;
      }
      default:
        break;
    }
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    processStep(input.trim());
  };

  const handleChip = (chipText) => {
    if (loading) return;
    processStep(chipText);
  };

  const currentChips = chips[chipSet] || [];
  const isDone = step === "done";

  return (
    <div data-testid="mirror-conversation" className="max-w-2xl mx-auto w-full px-3 sm:px-4 md:px-8 flex flex-col" style={{ height: 'calc(100vh - 120px)' }}>
      {/* Messages — scrollable */}
      <div className="flex-1 overflow-y-auto py-4 sm:py-6 space-y-3 sm:space-y-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            {msg.role === "mira" && (
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center mr-2 sm:mr-3 mt-1 flex-shrink-0" style={{ background: '#E91E8C' }}>
                <SparkleIcon size={12} className="text-white" />
              </div>
            )}
            <div
              className={`max-w-[85%] sm:max-w-[80%] rounded-2xl px-4 py-3 sm:px-5 sm:py-4 text-[14px] sm:text-sm leading-relaxed whitespace-pre-line ${
                msg.role === "user"
                  ? "bg-[#E91E8C]/10 text-[#16130F] rounded-br-sm"
                  : "bg-[#FBF7F0] text-[#16130F] rounded-bl-sm"
              }`}
              style={msg.role === "mira" ? { fontFamily: "var(--genz-font-display)", lineHeight: '1.7' } : {}}
            >
              {msg.text}
            </div>
          </div>
        ))}

        {/* Chips */}
        {showChips && currentChips.length > 0 && !loading && !isDone && (
          <div className="flex flex-wrap gap-2 pl-8 sm:pl-10 pt-1">
            {currentChips.map((chip) => (
              <button
                key={chip.label}
                onClick={() => handleChip(chip.text)}
                className="px-3.5 py-2 rounded-full text-xs font-sans font-medium border transition-all hover:scale-105"
                style={chip.style === "amber"
                  ? { background: '#E91E8C', color: '#fff', border: 'none' }
                  : { borderColor: '#E5E7EB', color: '#4B5563', background: 'transparent' }
                }
              >
                {chip.label}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-3 pl-10">
            <div className="flex gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E91E8C]/50 animate-bounce" />
              <span className="w-1.5 h-1.5 rounded-full bg-[#E91E8C]/50 animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-[#E91E8C]/50 animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input — fixed at bottom */}
      {!isDone && (
        <div className="flex-shrink-0 bg-white pt-3" style={{ borderTop: '0.5px solid #E5E7EB', paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))' }}>
          <form onSubmit={handleSubmit}>
          <div className="flex items-center gap-2 rounded-xl px-3 sm:px-4 py-1" style={{ background: '#FBF7F0', border: '0.5px solid #E5E7EB' }}>
            <input
              ref={inputRef}
              data-testid="mirror-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={step === "rating" ? "Pick a number 1-10..." : "Take your time..."}
              disabled={loading || step === "saving"}
              className="flex-1 bg-transparent text-[#16130F] text-sm font-sans placeholder:text-[#4B5563]/30 focus:outline-none py-3"
            />
            <button
              type="submit"
              disabled={loading || !input.trim() || step === "saving"}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-all disabled:opacity-30"
              style={{ background: input.trim() ? '#E91E8C' : 'transparent' }}
            >
              <Send size={14} className="text-white" />
            </button>
          </div>
          </form>
        </div>
      )}

      {/* Done — download + privacy */}
      {isDone && (
        <div className="flex-shrink-0 mt-4 pt-6 pb-8 text-center" style={{ borderTop: '0.5px solid #E5E7EB' }}>
          <a
            href={`${window.location.origin}/api/mirror/document/${token}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-mirror"
            className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full text-base font-sans font-semibold text-white transition-all hover:scale-105 no-underline"
            style={{ background: '#E91E8C', textDecoration: 'none' }}
          >
            Download my Mirror &rarr;
          </a>
          <p className="mt-6 font-sans text-xs italic" style={{ color: '#888780' }}>
            Your Mirror belongs entirely to you. Mira reads it to reflect it back — and then it stays in your hands. We will never share it, sell it, or show it to anyone — including your employer. Ever.
          </p>
          <button
            data-testid="mirror-reload"
            onClick={() => window.location.reload()}
            className="mt-4 text-xs font-sans text-[#E91E8C] hover:underline"
          >
            View completed Mirror
          </button>
        </div>
      )}
    </div>
  );
}

/* Gate page for people without a story */
function MirrorGate() {
  return (
    <div data-testid="mirror-gate" className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: '#E91E8C' }}>
          <SparkleIcon size={24} className="text-white" />
        </div>
        <h2 className="text-2xl font-medium text-[#16130F] mb-3">
          The Mirror opens after you've shared your why.
        </h2>
        <p className="font-sans text-base mb-8" style={{ color: '#4B5563', lineHeight: '1.8' }}>
          It's the next conversation — the private one.
        </p>
        <button
          data-testid="mirror-gate-cta"
          onClick={() => {
            const orb = document.querySelector('[data-testid="mira-orb"]');
            if (orb) orb.click();
          }}
          className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full text-base font-sans font-semibold text-white transition-all hover:scale-105"
          style={{ background: '#E91E8C' }}
        >
          Share your why first
          <ArrowRight size={18} />
        </button>
        <p className="mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '11px', color: '#888780', fontStyle: 'italic' }}>
          Mira is the one who knows — she listens, she asks, she remembers.
        </p>
      </div>
    </div>
  );
}

/* Mirror Landing page (no token) */
function MirrorLanding() {
  const currentYear = new Date().getFullYear();
  const fy = `${currentYear}-${String(currentYear + 1).slice(2)}`;

  return (
    <div data-testid="mirror-landing" className="min-h-screen" style={{ background: '#16130F' }}>
      {/* Dark hero */}
      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold mb-6" style={{ color: '#E91E8C' }}>
            Your private space
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl leading-snug mb-6" style={{ fontFamily: "var(--genz-font-display)", color: '#fff' }}>
            You've shared your why with the world.
            <br />
            <span style={{ color: '#E91E8C' }}>Now — how honest are you willing to be with yourself?</span>
          </h1>
          <p className="max-w-xl mx-auto mb-10" style={{ fontFamily: "var(--genz-font-display)", fontSize: '17px', lineHeight: '1.9', fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>
            The Mirror is a private self-appraisal — not for your manager, not for your organisation, not for the wall. Just for you. Mira walks you through your year — what landed, what didn't, what you're building toward — and reflects back what she sees. It stays entirely between you and her.
          </p>
          <div className="space-y-3 mb-10 inline-block text-left">
            {[
              "Only accessible after you've shared your why",
              "Never shown publicly. Never shared. Ever.",
              "One conversation a year. Worth every minute.",
            ].map((line) => (
              <div key={line} className="flex items-center gap-3">
                <span style={{ color: '#E91E8C', fontSize: '12px' }}>&#10022;</span>
                <span className="text-sm font-sans" style={{ color: 'rgba(255,255,255,0.6)' }}>{line}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* A Glimpse Inside */}
      <section data-testid="mirror-preview" className="pb-20 md:pb-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold mb-3" style={{ color: '#E91E8C' }}>
              A glimpse inside
            </p>
            <p className="font-sans text-base md:text-lg" style={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
              What The Mirror looks like — five sections, one conversation, three reflections. Just you and Mira.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left — Conversation mockup */}
            <div className="rounded-2xl overflow-hidden" style={{ background: '#2C2C2A' }}>
              <div className="px-6 md:px-8 py-7 md:py-8">
                <p className="text-xs uppercase tracking-[0.15em] font-sans font-semibold mb-5" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  The conversation
                </p>

                {/* Mock messages */}
                <div className="space-y-4 mb-6">
                  {/* Mira */}
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#E91E8C' }}>
                      <SparkleIcon size={11} className="text-white" />
                    </div>
                    <div className="rounded-2xl rounded-bl-sm px-4 py-3 text-sm" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.75)', fontFamily: "var(--genz-font-display)", lineHeight: '1.7' }}>
                      Let's look back at the last 12 months. Where did your work have real, visible impact?
                    </div>
                  </div>
                  {/* User */}
                  <div className="flex justify-end">
                    <div className="rounded-2xl rounded-br-sm px-4 py-3 text-sm max-w-[80%]" style={{ background: 'rgba(233,30,140,0.15)', color: 'rgba(255,255,255,0.8)', lineHeight: '1.7' }}>
                      I rebuilt our onboarding flow. Retention went from 40% to 68%. That was the thing that mattered most.
                    </div>
                  </div>
                  {/* Mira response */}
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#E91E8C' }}>
                      <SparkleIcon size={11} className="text-white" />
                    </div>
                    <div className="rounded-2xl rounded-bl-sm px-4 py-3 text-sm" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.75)', fontFamily: "var(--genz-font-display)", lineHeight: '1.7' }}>
                      40% to 68%. You didn't just improve something — you changed the odds for every person who came after. Now the hard one. What genuinely failed?
                    </div>
                  </div>
                </div>

                {/* Self-ratings mockup */}
                <div className="mt-6 pt-5" style={{ borderTop: '0.5px solid rgba(255,255,255,0.08)' }}>
                  <p className="text-xs uppercase tracking-[0.15em] font-sans font-semibold mb-4" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    Your honest numbers
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { k: "Impact", v: 8 },
                      { k: "Innovation", v: 7 },
                      { k: "Ownership", v: 9 },
                      { k: "Collaboration", v: 6 },
                      { k: "Growth", v: 7 },
                      { k: "Reliability", v: 8 },
                    ].map((r) => (
                      <div key={r.k} className="text-center py-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
                        <p className="text-lg font-sans font-semibold" style={{ color: '#E91E8C' }}>{r.v}</p>
                        <p className="text-[10px] uppercase tracking-[0.1em] font-sans" style={{ color: 'rgba(255,255,255,0.35)' }}>{r.k}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* One word */}
                <div className="mt-5 pt-4 text-center" style={{ borderTop: '0.5px solid rgba(255,255,255,0.08)' }}>
                  <p className="text-xs uppercase tracking-[0.1em] font-sans mb-1" style={{ color: 'rgba(255,255,255,0.3)' }}>Your word</p>
                  <p className="text-xl font-sans font-semibold" style={{ color: '#E91E8C' }}>Clarity</p>
                </div>
              </div>
            </div>

            {/* Right — Mira's reflections */}
            <div className="space-y-6">
              {/* Reflection card */}
              <div className="rounded-2xl overflow-hidden" style={{ background: '#1a1a2e' }}>
                <div className="px-6 md:px-8 py-7 md:py-8">
                  <div className="flex items-center gap-2.5 mb-5">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#E91E8C' }}>
                      <SparkleIcon size={11} className="text-white" />
                    </div>
                    <p className="text-xs uppercase tracking-[0.15em] font-sans font-semibold" style={{ color: '#E91E8C' }}>What Mira sees</p>
                  </div>
                  <p className="font-sans text-sm leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.75)', lineHeight: '1.85', fontStyle: 'italic' }}>
                    "You rated yourself 6 on collaboration. But everything you wrote about this year suggests someone who finishes what they start — even when it's hard, even when the team around you wasn't ready. I wonder if the 6 is too honest, or not honest enough about what honest actually means."
                  </p>
                  <div className="py-4" style={{ borderTop: '0.5px solid rgba(255,255,255,0.08)' }}>
                    <p className="text-xs uppercase tracking-[0.1em] font-sans font-semibold mb-2" style={{ color: '#E91E8C' }}>Your theme</p>
                    <p className="font-sans text-base" style={{ color: 'rgba(255,255,255,0.85)', fontStyle: 'italic' }}>
                      This was the year you stopped waiting for permission.
                    </p>
                  </div>
                  <div className="pt-4" style={{ borderTop: '0.5px solid rgba(255,255,255,0.08)' }}>
                    <p className="text-xs uppercase tracking-[0.1em] font-sans font-semibold mb-2" style={{ color: '#E91E8C' }}>Your word: Clarity</p>
                    <p className="font-sans text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)', lineHeight: '1.8' }}>
                      "You chose Clarity. What's interesting is that everything you wrote suggests you already have it — you're just not sure you're allowed to trust it yet."
                    </p>
                  </div>
                </div>
              </div>

              {/* Word Journey */}
              <div data-testid="word-journey-preview" className="rounded-2xl overflow-hidden" style={{ background: '#2C2C2A' }}>
                <div className="px-6 md:px-8 py-6 md:py-7">
                  <p className="text-xs uppercase tracking-[0.15em] font-sans font-semibold mb-5" style={{ color: '#E91E8C' }}>
                    Your journey in words
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <span className="text-xs font-sans font-semibold w-16" style={{ color: 'rgba(255,255,255,0.5)' }}>{fy}</span>
                      <span className="px-4 py-1.5 rounded-full text-sm font-sans font-semibold" style={{ background: '#E91E8C', color: '#fff' }}>
                        Clarity
                      </span>
                    </div>
                    {[1, 2, 3, 4].map((offset) => {
                      const futureYear = currentYear + offset;
                      const futureFy = `${futureYear}-${String(futureYear + 1).slice(2)}`;
                      return (
                        <div key={futureFy} className="flex items-center gap-4">
                          <span className="text-xs font-sans font-semibold w-16" style={{ color: 'rgba(255,255,255,0.2)' }}>{futureFy}</span>
                          <span className="px-4 py-1.5 rounded-full text-xs font-sans" style={{ border: '0.5px dashed rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.2)' }}>
                            waiting...
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Privacy + CTA */}
          <div className="mt-14 text-center">
            <p className="font-sans text-xs italic mb-8" style={{ color: 'rgba(255,255,255,0.3)' }}>
              Your Mirror belongs entirely to you. Mira reads it to reflect it back — and then it stays in your hands. We will never share it, sell it, or show it to anyone — including your employer. Ever.
            </p>
            <MirrorGate />
          </div>
        </div>
      </section>
    </div>
  );
}

/* Main Mirror Page */
export default function MirrorPage() {
  const { token } = useParams();
  const [storyInfo, setStoryInfo] = useState(null);
  const [completedData, setCompletedData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    const verify = async () => {
      try {
        const res = await axios.get(`${API}/mirror/verify/${token}`);
        if (res.data.valid) {
          setStoryInfo(res.data);
          if (res.data.completed) {
            const mirrorRes = await axios.get(`${API}/mirror/completed/${token}`);
            setCompletedData(mirrorRes.data);
          }
        }
      } catch {}
      setLoading(false);
    };
    verify();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-[#E91E8C]" />
      </div>
    );
  }

  // No token — show landing page
  if (!token) {
    return <MirrorLanding />;
  }

  // Invalid token
  if (!storyInfo || !storyInfo.valid) {
    return <MirrorGate />;
  }

  // Completed — show results
  if (completedData) {
    return (
      <div className="min-h-screen bg-white">
        <CompletedMirror data={completedData} token={token} />
      </div>
    );
  }

  // Active — show conversation
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 md:px-12 pt-6 sm:pt-8 pb-2 sm:pb-4">
        <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold" style={{ color: '#E91E8C' }}>
          The Mirror &middot; {storyInfo.person_name}
        </p>
      </div>
      <div className="flex-1 overflow-hidden">
        <MirrorConversation storyInfo={storyInfo} token={token} />
      </div>
    </div>
  );
}
